import linkifyHtml from 'linkify-html';
import {
    AresField,
    AresViewField,
    Association,
    AsyncSelect,
    Authority,
    Category,
    CheckBox,
    DataBox,
    DateDiff,
    DatePicker,
    DateRange,
    FilePicker,
    IsoTransitions,
    NodeRef,
    RichText,
    Select,
    Size,
    Table,
    TextField,
    Transitions,
} from '../Controls';
import { validateField } from '../../lib/validation';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Label from '../Label/Label';
import {
    addConstraint,
    fetchOptions,
    highlightCurrentField,
    highlightErrors,
    makeFieldDirty,
    onFormChange,
    removeConstraint,
} from '../../actions/form';
import { isEmpty, forEachObjIndexed, is, path } from 'ramda';
import { Constraint, FieldJson, FormJson } from 'edocatbase/Form';
import { Dispatch } from 'redux';
import { getSearchURL } from '../../lib/searchUrl';
import {  Icon, Link  } from "@mui/material";
import {getCustomFieldValue} from '../../lib/customModifyForm';

interface FieldComponentInterface extends FormJson {
	actions: any;
	constraints: [];
	field: FieldJson;
	formInstanceId: string;
	innerFormInstanceId?: string;
	docgenform?: boolean | undefined;
	readOnly: boolean;
}

class Field extends window.React.PureComponent<FieldComponentInterface> {
	doCollateralChange = (fieldName: string, value: any) => {
		const { fields } = this.props;

		const collateralChange = fields[fieldName].collateralChange;

		if(collateralChange.getDisabled){
			collateralChange.fields.forEach((field:string, index: number) => {
				this.onChange([field, 'disabled'], collateralChange.getDisabled(field, value), true, false, false);
			})
		};

		if (collateralChange.transformValue) {
			const newValues = collateralChange.transformValue({
				[fieldName]: value,
				...collateralChange.fields.reduce(
					(acc: { [key: string]: any }, row: string) => ({
						...acc,
						[row]: fields[row].value || '',
					}),
					{}
				),
			});

			forEachObjIndexed((value: any, name) => {
				this.onChange([String(name)], value, true);
			}, newValues);
		} else {
			collateralChange.fields.forEach((field: string, index: number) => {
				if (collateralChange.getValue) {
					const variables = collateralChange.currentProperties || [];
					const data: { [key: string]: any } = {};

					variables.forEach((vrbl: keyof FieldJson) => {
						data[vrbl] = fields[field][vrbl];
					});
					this.onChange([field], collateralChange.getValue(field, value, data), true);
				} else {
					const equalField = collateralChange.equalFields[index];
					let newValue = null;

					if (value && value[equalField]) {
						if (is(Array, value[equalField])) {
							newValue = value[equalField].map((it: any) => {
								if (is(Object, it)) {
									let label = it?.['cm:title'];
									if (it?.type === 'cm:person') {
										label = `${it?.['cm:firstName']} ${it?.['cm:lastName']} (${it?.['cm:userName']}) `;
									}
									if (it?.type === 'cm:authorityContainer') {
										label = it?.['cm:authorityDisplayName'];
									}
									return { label: label, value: it?.nodeRef, type: it?.type };
								} else {
									const options = fields[field].options as Array<any>;
									return options?.find(option => option?.value === String(it));
								}
							});
						} else if (value[equalField]?.nodeRef) {
							let label = value[equalField]?.['cm:title'];
							if (value[equalField]?.type === 'cm:person') {
								label = `${value[equalField]?.['cm:firstName']} ${value[equalField]?.['cm:lastName']} (${value[equalField]?.['cm:userName']}) `;
							}
							if (value[equalField]?.type === 'cm:authorityContainer') {
								label = value[equalField]?.['cm:authorityDisplayName'];
							}
							newValue = { label: label, value: value[equalField]?.nodeRef };
						} else if (fields[field]?.repeating && !Array.isArray(value[equalField])) {
							newValue = value[equalField].split(',');
						} else if (value[equalField] === 'true' || value[equalField] === 'false') {
							newValue = value[equalField];
						} else {
							newValue =
								fields[field]?.dataType === 'date' || fields[field]?.dataType === 'datetime'
									? value[equalField]
									: String(value[equalField]);
						}
					}
					
					if (
						value === null &&
						fields[field] !== undefined &&
						!fields[field].repeating &&
						(fields[field].dataType === 'text' ||
							fields[field].dataType === 'int' ||
							fields[field].dataType === 'long' ||
							fields[field].dataType === 'double')
					) {
						newValue = '';
					}
					this.onChange([field], newValue, true, true);
				}
			});
		}
	};

	applyMandatory = (fieldName: string, value: any, otherFields?: string) => {
		const { actions, fields, innerFormInstanceId, formInstanceId, constraints } = this.props;

		const mandFields = otherFields?.split(',') || [];
		const fieldId = fields[fieldName].control.params.applyMandatoryIfNotBlank;

		const constraint = {
			fieldId,
			id: 'MANDATORY',
		};

		if (mandFields.some(it => fields[it]?.value) || value) {
			actions.addConstraint(formInstanceId, constraint, innerFormInstanceId);
			//To remove accidental error messages/form blocks
			this.doValidate(fieldId, fields[fieldId].value, [...constraints, constraint]);
		} else {
			actions.removeConstraint(formInstanceId, constraint, innerFormInstanceId);
			//To remove accidental error messages/form blocks
			this.doValidate(
				fieldId,
				fields[fieldId].value,
				constraints.filter(item => (item.fieldId === fieldId ? item.id !== 'MANDATORY' : true))
			);
		}
	};

	updateOptions = (fieldName: string, value: any) => {
		const { actions, fields, formInstanceId, innerFormInstanceId } = this.props;
		const { field, url } = fields[fieldName].updateOptionsOnChange;
		const params = fields[field].control.params;
		const changeValue = Array.isArray(value) ? value.map(item => item.value).join(',') : value.value;
		params.fetchUrl = `${url}${changeValue}`;
		actions.fetchOptions(formInstanceId, params, field, innerFormInstanceId);
	};

	doValidate = (fieldName: string, value: any, newConstraints?: Constraint[]) => {
		const { actions, constraints, fields, formInstanceId, innerFormInstanceId } = this.props;

		const validation = validateField(
			value,
			(newConstraints || constraints).filter((item: Constraint) => item.fieldId === fieldName),
			fields
		);

		actions.highlightCurrentField(formInstanceId, fieldName, validation, innerFormInstanceId);
	};

	onIn = (name: string, data: any) => {
		const { fields } = this.props;
		const {
			field: {
				control: {
					params: { customQueryUpdateValue },
				},
			},
		} = this.props;
		if (customQueryUpdateValue) {
			this.onChange([customQueryUpdateValue?.target], fields[customQueryUpdateValue?.source]?.value, true);
		}
	};

	onChange = (name: string[], value: any, dirty: boolean, stopPropagate = false, addValueField = true) => {
		const fieldName = name[0];

		const { actions, fields, formInstanceId, innerFormInstanceId } = this.props;

		if (!fields[fieldName]) return;

		actions.onFormChange(formInstanceId, name, value, innerFormInstanceId, addValueField);

		if (!stopPropagate && fields[fieldName] && fields[fieldName].collateralChange) {
			this.doCollateralChange(fieldName, value);
		}

		if (fields[fieldName] && fields[fieldName]?.control?.params?.applyMandatoryIfNotBlank) {
			this.applyMandatory(fieldName, value, fields[fieldName]?.control?.params?.notBlankFields);
		}

		if (fields[fieldName] && !isEmpty(value) && fields[fieldName]?.updateOptionsOnChange) {
			this.updateOptions(fieldName, value);
		}

        const customQueryUpdateOptionsTarget = fields[fieldName]?.control?.params?.customQueryUpdateOptionsTarget;
        if (fields[fieldName] && Array.isArray(customQueryUpdateOptionsTarget)) {
            for (const targetName of customQueryUpdateOptionsTarget) {
                const targetField = fields[targetName];
                targetField.control.params.customQuery.srcValue = getCustomFieldValue(value);
                actions.makeFieldDirty(formInstanceId, targetName, innerFormInstanceId);
                this.onChange([targetName], '', true);
            }
		}

		if (fields[fieldName]?.control?.params?.onChange) {
			fields[fieldName].control.params.onChange(value);
		}

		this.doValidate(fieldName, value);
	};

	onBlur = (name: string, value: any, dirty: boolean) => {
		const { actions, formInstanceId, innerFormInstanceId } = this.props;

		if (!dirty) {
			actions.makeFieldDirty(formInstanceId, name, innerFormInstanceId);
		}

		this.doValidate(name, value);
	};

	render() {
		const {
			noValidate,
			type,
			formInstanceId,
			innerFormInstanceId,
			field: propsField,
			readOnly,
			setDisabled,
			docgenform,
			getPropBpmContext
		} = this.props;

		const field = propsField?.helpText
			? {
					...propsField,
					label: <Label title={propsField.helpText} text={propsField.label} />,
					important: readOnly ? '' : propsField.important,
			  }
			: {
					...propsField,
					important: readOnly ? '' : propsField.important,
			  };

		const {
			name,
			control: { template, params },
			dataType,
			configName,
			value,
		} = field;

		const disabled = field.disabled || setDisabled;

		if (
			readOnly &&
			(template.endsWith('content.ftl') ||
				template.endsWith('hidden.ftl') ||
				template.endsWith('transitions.ftl') ||
				template.endsWith('mimetype.ftl') ||
				params?.display === 'hidden')
		) {
			return null;
		}

		//if (name === 'prop_cm_taggable') return null;

		if (
			!readOnly &&
			(template.endsWith('mimetype.ftl') ||
				template.endsWith('size.ftl') ||
				template.endsWith('hidden.ftl') ||
				template.endsWith('content.ftl') ||
				params?.display === 'hidden')
		)
			return null;

		if (
			(name === 'prop_cm_modified' ||
				name === 'prop_cm_modifier' ||
				name === 'prop_cm_created' ||
				name === 'prop_cm_creator' ||
				template.endsWith('tableMM.ftl')) &&
			type === 'search'
		)
			return null;

		if (params?.display === 'hidden') return null;

		const error = Boolean(field.error)
			? window.Alfresco.messages.global['Alfresco.forms.validation.' + field.error + '.message'] || field.error
			: null;

		const important = Boolean(field.important) ? field.important : null;

		const className = Boolean(error) ? 'invalid' : Boolean(important) && !readOnly ? 'important' : '';

		const mandatory = readOnly || noValidate ? false : field.mandatory && type !== 'search';

		//View mode only
		if (template.endsWith('size.ftl')) {
			return <Size {...{ ...field, className }} />;
		}

		//Edit mode only
		if (template.endsWith('/transitions.ftl')) {
			return (
				<Transitions
					{...{ ...field, mandatory, className, formInstanceId, innerFormInstanceId }}
					onChange={this.onChange}
					disabled={disabled}
				/>
			);
		}

		//Edit mode only
		if (template.endsWith('activiti-transitions.ftl')) {
			return (
				<IsoTransitions
					{...{ ...field, mandatory, className, formInstanceId, innerFormInstanceId }}
					onChange={this.onChange}
					disabled={disabled}
				/>
			);
		}

		if (template.endsWith('richtext.ftl')) {
			return (
				<RichText
					{...{ ...field, mandatory, className, error, params }}
					onBlur={this.onBlur}
					onChange={this.onChange}
					readOnly={readOnly}
					disabled={disabled}
				/>
			);
		}

		if (template.endsWith('category.ftl')) {
			return (
				<Category
					{...{ ...field, mandatory, className, error, formInstanceId, innerFormInstanceId }}
					onBlur={this.onBlur}
					onChange={this.onChange}
					readOnly={readOnly}
					disabled={disabled}
				/>
			);
		}

		if (
			template.endsWith('selectone.ftl') ||
			template.endsWith('priority.ftl') ||
			template.endsWith('taskowner.ftl') ||
			template.endsWith('selectmany.ftl')
		) {
			return (
				<Select
					{...{ ...field, mandatory, className, error, formInstanceId, innerFormInstanceId }}
					onBlur={this.onBlur}
					onChange={this.onChange}
					repeating={template.endsWith('selectmany.ftl')}
					readOnly={readOnly}
					disabled={disabled}
				/>
			);
		}

		if (template.endsWith('association.ftl')) {
			return (
				<Association
					{...{
						...field,
						mandatory,
						className,
						error,
						formInstanceId,
						innerFormInstanceId,
						selfId: this.props.arguments?.itemId,
					}}
					readOnly={readOnly}
					disabled={disabled}
				/>
			);
		}

		if (template.endsWith('packageitems.ftl')) {
			return (
				<Association
					{...{ ...field, mandatory, className, error, formInstanceId, innerFormInstanceId }}
					readOnly={readOnly}
					disabled={disabled}
					validate={this.doValidate}
					getPropBpmContext={getPropBpmContext}
				/>
			);
		}

		//Edit mode only
		if (
			template.endsWith('daterange.ftl') ||
			((template.endsWith('date.ftl') || dataType === 'date') && type === 'search')
		) {
			return (
				<DateRange
					{...{ ...field, mandatory, className, error }}
					onBlur={this.onBlur}
					onChange={this.onChange}
					disabled={disabled}
				/>
			);
		}

		// if (template.endsWith('numberrange.ftl') && type === 'search') {
		//   return <NumberRange {...{ className, name, error, mandatory }} onChange={this.onChange} />;
		// }

		//Edit mode only
		if (template.endsWith('datediff.ftl')) {
			return (
				<DateDiff
					{...{ ...field, mandatory, className, error, formInstanceId, innerFormInstanceId }}
					onBlur={this.onBlur}
					onChange={this.onChange}
					disabled={disabled}
				/>
			);
		}

		if (template.endsWith('date.ftl') || dataType === 'date') {
			return (
				<DatePicker
					{...{ ...field, mandatory, className, error }}
					onBlur={this.onBlur}
					onChange={this.onChange}
					readOnly={readOnly}
					disabled={disabled}
					docgenform={docgenform}
				/>
			);
		}

		if ((template.endsWith('checkbox.ftl') || template.endsWith('email-notification.ftl')) && type !== 'search') {
			return (
				<CheckBox
					{...{ ...field, mandatory, className, error }}
					onChange={this.onChange}
					readOnly={readOnly}
					disabled={disabled}
				/>
			);
		}

		//Edit mode only
		if ((template.endsWith('checkbox.ftl') || template.endsWith('email-notification.ftl')) && type === 'search') {
			const checkBoxOptions = [
				{
					label: window.Alfresco.messages.global['label.no'],
					value: 'false',
				},
				{
					label: window.Alfresco.messages.global['label.yes'],
					value: 'true',
				},
			];
			return (
				<Select
					{...{ ...field, mandatory, className, error, formInstanceId, innerFormInstanceId }}
					onBlur={this.onBlur}
					onChange={this.onChange}
					options={checkBoxOptions}
					value={(field.value === null || field.value === undefined) ? null : checkBoxOptions[String(field.value) === 'true' || String(field.value.value) === 'true' ? 1 : 0]}
					disabled={disabled}
				/>
			);
		}

		if (template.endsWith('authority.ftl')) {
			return (
				<Authority
					{...{ ...field, mandatory, className, error, formInstanceId, innerFormInstanceId }}
					onBlur={this.onBlur}
					onChange={this.onChange}
					readOnly={readOnly}
					disabled={disabled}
					docgenform={docgenform}
				/>
			);
		}

		//Edit mode only
		if (template.endsWith('filepicker.ftl')) {
			return (
				<FilePicker
					{...{ ...field, mandatory, className, error }}
					onBlur={this.onBlur}
					onChange={this.onChange}
					disabled={disabled}
				/>
			);
		}

		if (template.endsWith('asyncselect.ftl')) {
			return (
				<AsyncSelect
					{...{ ...field, mandatory, className, error, formInstanceId, innerFormInstanceId }}
					onBlur={this.onBlur}
					documentNodeRef={this.props.arguments?.itemId ?? null}
					onChange={this.onChange}
					readOnly={readOnly}
					disabled={disabled}
					searchDisabled={type === 'search'}
				/>
			);
		}

		if (template.endsWith('databox.ftl')) {
			return (
				<DataBox
					{...{ ...field, mandatory, className, error, formInstanceId }}
					onBlur={this.onBlur}
					onChange={this.onChange}
					readOnly={readOnly}
					disabled={disabled}
				/>
			);
		}

		if (template.endsWith('noderef.ftl')) {
			return (
				<NodeRef
					{...{ ...field, mandatory, className, error, formInstanceId, innerFormInstanceId }}
					readOnly={readOnly}
					disabled={disabled}
				/>
			);
		}

		if (template.endsWith('aresfield.ftl') && type !== 'search') {
			// @ts-ignore
			return readOnly ? (
				<AresViewField {...{ ...field, formInstanceId, className, innerFormInstanceId, params }} view />
			) : (
				<AresField
					{...{ ...field, mandatory, className, error, params, formInstanceId }}
					onBlur={this.onBlur}
					onChange={this.onChange}
					readOnly={readOnly}
					disabled={disabled}
				/>
			);
		}

		if (template.endsWith('tableMM.ftl')) {
			return (
				<Table
					{...{ ...field, mandatory, className, error, formInstanceId, innerFormInstanceId, params }}
					view={readOnly}
					disabled={disabled}
				/>
			);
		}

		if (template.endsWith('number.ftl') || template.endsWith('double.ftl')) {
			return (
				<TextField
					{...{ ...field, mandatory, className, error }}
					onBlur={this.onBlur}
					onChange={this.onChange}
					onIn={this.onIn}
					readOnly={readOnly}
					url={getSearchURL(configName, value)}
					disabled={disabled}
				/>
			);
		}

		// Edit mode Text
		if (!readOnly && (template.endsWith('textarea.ftl') || template.endsWith('textfield.ftl') || template.endsWith('aresfield.ftl'))) {
			return (
				<TextField
					{...{ ...field, mandatory, className, error }}
					multiline={template.endsWith('textarea.ftl')}
					onBlur={this.onBlur}
					onChange={this.onChange}
					onIn={this.onIn}
					readOnly={readOnly}
					url={getSearchURL(configName, value)}
					disabled={disabled}
				/>
			);
		}

		// View mode on TextFields & Textareas

		const linkOptions = {
			defaultProtocol: 'https',
			target: '_blank',
			className: 'edcMetaLink',
			validate: {
				url(value:string) {
					return /^(http|ftp)s?:\/\//.test(value);
				},
				email(value:string) {
					return /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/.test(value);
				}
			}
		};

		const fieldValue =
			typeof field.value === 'string' && !field?.configName?.includes("cm:name")
				? linkifyHtml(field.value.replace('<', '&lt;').replace('>', '&gt;'), linkOptions)
				: field.value;

		if (readOnly && (template.endsWith('textarea.ftl'))) {
			return (
				<div className={`form-row simple-row ${disabled ? 'read-only-row' : ''} row-${field.name}`}>
					<label>{field.label}</label>
					<div
						style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
						id={field.name}
						dangerouslySetInnerHTML={{
							__html: fieldValue || window.Alfresco.messages.global['label.none'],
						}}
					/>
				</div>
			);
		}

		const isAspect = field.name.startsWith('prop_mmAspect');

		return (
			<div className={`form-row simple-row ${disabled ? 'read-only-row' : ''} row-${field.name}`}>
				<label>{field.label}</label>
				{(isAspect && field.value) ? (
					<div>
						<a href={getSearchURL(configName, field.value)} target="_blank" className="search-link" id={field.name}>
							<Icon className="fas fa-search" fontSize="small" />
							{field.value}
						</a>
					</div>
				) : (
					<div
						id={field.name}
						dangerouslySetInnerHTML={{
							__html: fieldValue || window.Alfresco.messages.global['label.none'],
						}}
					/>
				)}
			</div>

		);
	}
}

const mapStateToProps = (state: any, props: any) => {
	let pathToForm: string[] = ['form'];
	if (props.innerFormInstanceId) {
		pathToForm = [props.formInstanceId, ...pathToForm, props.innerFormInstanceId];
	} else {
		pathToForm = [props.formInstanceId, ...pathToForm];
	}

	const form: any = path(pathToForm, state);

	return {
		field: form?.fields[props.fieldName],
		...form,
	};
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
	actions: bindActionCreators(
		{
			onFormChange,
			highlightErrors,
			addConstraint,
			removeConstraint,
			fetchOptions,
			makeFieldDirty,
			highlightCurrentField,
		},
		dispatch
	),
});

export default connect(mapStateToProps, mapDispatchToProps)(Field);
